// v0.99.9 — the embedded slider: "Pay with M®" inside a merchant's page.
//
// A merchant's page can't use M's passkey (it is bound to mdrop.it), so the
// slider on their checkout is THIS page in an iframe (allow="publickey-
// credentials-get publickey-credentials-create"), mounted by /m.js:
//   mdrop.it/embed/pay?to=CODE&amount=0.49&ccy=USD&ref=OA7K3M
// It is the pass card reduced to one line — amount, the merchant as M knows
// them (never as the page says), the reference — and the slide. Ground: Pure
// with the guilloche, whatever the customer's theme, so M's row is the one
// with a texture in any list of payment methods.
//
// app 1.5.27 (Alexander, 27 Oct; the mock-ups "M® Checkout — the first payment, handed to M", rounds 1–5): the slider
// is a BUTTON here — M blue (M's logo colour on Pure), the words and an arrow: "Pay with M® ↗" while this frame doesn't
// know the wallet (a real link to M's own pay page in a named tab, which no browser blocks; the first payment is made
// there, in Midnight Cobalt, and M's tab hands the wallet back — handoff.ts), "Pay with M® →" once it does (one tap, one
// Touch ID, in place). A tap here turns the frame Midnight Cobalt — Paying… in M's neon green, then Paid — taking the
// person into M's world; the rest of M's panel stays quiet on the merchant's page. Touch ID confirms the payment; the
// amount and the payee stand above the button. Inside M (the pay page, Sending, Swap) the slider stays.
//
// It talks to the host page by postMessage: ready · height · paid (bundler
// accepted) · settled (receipt) · failed · cancelled · open (asks the host to
// open the full pay page — the fallback when the frame can't sign). The host
// must confirm a payment with M's Worker, never with these messages.

import type { WalletAdapter } from "../adapters/WalletAdapter";
import { getProfile, recordTx, resolveName } from "../api";
import { reconcile } from "../settings"; // 1.5.50
import { emojiFor } from "../emoji";
import { formatCode } from "../codes";
import { canonicalAmount, formatUsdShort } from "../money";
import { formatFx } from "../fx";
import { clearCredential, readCredential, saveCredential } from "../credentialStore";
import { fxFor, needLine, planPay, payWithTopUp, sourceLine, type PayPlan } from "../payLadder"; // 1.4.58
import { setFlag } from "../deviceFlag";
import { failureLine, FAIL_FUNDS, isPasskeyCancel, recordError } from "../diag";
import { handoffTarget, readHandoff } from "../handoff"; // 1.5.27
import { needsCard, saveCard } from "../card"; // 1.5.48; 1.5.51 cardFromSettings — above the limit the frame hands the payment to M's tab, where the card is taken
import { PREPARED_TTL_MS, sponsorOnTouch, type PreparedOp } from "../adapters/WalletAdapter";
import { brandedQrSvg } from "../qr";
import { IC_X } from "../icons";
import { LOGO_M } from "../logo"; // 1.5.34 — M's logo top left of the checkout's panel
import { merchantCheck, mismatchLine, payStatus } from "../api";
import { IC_BC_WARNING } from "../broadcast"; // 1.5.21 — Alexander's warning glyph, in the alert colour
import { playDoneElement, primeDoneElement } from "../sound";
import { cachedProfile, rememberProfile } from "../profileCache"; // 1.5.39 // 1.5.37 — M's "done" at the checkout, from an <audio> element

interface EmbedDeps {
  adapter: WalletAdapter;
  to: string; // the merchant's wallet code
  amount: number;
  currency: string;
  reference?: string;
  /** 1.5.21 — the merchant's page as the browser reports it; the harness passes one (an unframed page has none). */
  site?: string | null;
}

/** 1.5.21 — the page this frame sits on, as the browser says (never as the page says): the referrer, which carries
 *  the full URL for a same-origin parent (the demos) and the origin for another site under the default policy; the
 *  parent's origin from ancestorOrigins where the browser has it, which wins if the two disagree. */
export function hostSite(key = ""): string | null {
  const anc = (location as unknown as { ancestorOrigins?: { length: number; [i: number]: string } }).ancestorOrigins;
  const parent = anc && anc.length ? anc[0] : null;
  // 1.5.24 — a frame that reloads itself has its own address as the referrer (seen 26 Oct when a new version went live
  // under the café's checkout: the warning vanished). Never take M's own frame for the site; keep the first site seen
  // for this order in the tab, and fall back to it.
  const mem = `m.embedSite.${key}`;
  try {
    if (document.referrer) {
      const u = new URL(document.referrer);
      const own = u.origin === location.origin && u.pathname.startsWith("/embed/");
      if (!own && (!parent || u.origin === parent)) {
        const site = u.origin + u.pathname;
        try { sessionStorage.setItem(mem, site); } catch { /* storage refused */ }
        return site;
      }
    }
  } catch { /* fall through */ }
  try { const kept = sessionStorage.getItem(mem); if (kept) return kept; } catch { /* storage refused */ }
  return parent ? parent + "/" : null;
}

function post(msg: Record<string, unknown>) {
  try {
    window.parent.postMessage({ m: "pay", ...msg }, "*"); // no secrets travel here; the host verifies server-side
  } catch { /* not framed */ }
}

/** 1.5.27 — a cancelled Face ID, in the button's words (the app's line says "Slide again"). */
// 1.5.52 (Alexander, 1 Nov) — "Security checks didn't complete. Try again." (no sensor named; was "Face ID didn't complete.
// Tap again when you're ready."), and the raw error line under it is gone (HL-3); the error still goes to Lab and to the merchant's page.
const TAP_AGAIN_LINE = "Security checks didn't complete. Try again.";

export function renderEmbedPayView(root: HTMLElement, { adapter, to, amount, currency, reference, site: siteIn }: EmbedDeps) {
  const ccy = (currency || "USD").toUpperCase();
  const fmtAmt = (n: number) => (ccy !== "USD" ? formatFx(ccy, n) : formatUsdShort(n));
  const amountStr = canonicalAmount(amount);
  const slug = to;
  const payPageUrl = `${location.origin}/${formatCode(slug)}/${ccy}${amountStr}${reference ? "/" + encodeURIComponent(reference) : ""}`;
  // 1.5.17 (Alexander, 26 Oct) — on a computer the way to pay is the phone: M's QR of the pay link (Apple Pay style),
  // scanned with the phone's camera; the phone pays on the pay page, and this frame learns of it from the Worker
  // (GET /pay/status by the payee and the merchant's reference) and raises "paid" / "settled" to the page exactly as
  // a slide here would. 1.5.27 — the QR, "or", then the button, on every computer (M's tab handles a computer that
  // can't sign: the passkey from the phone, or the QR there too).
  const onComputer = !(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  // 1.5.54 (Alexander, 1 Nov: "Apple Pay pushes everything to the environment it controls") — on a phone every payment is
  // made in M's tab, on M's own origin (the address bar shows it; a frame on a merchant's page could be imitated): the
  // button is always M's tab's link, whatever this frame knows. A computer keeps the QR for the phone and the button.
  const handOnly = !onComputer;
  // 1.5.21 (Alexander, 26 Oct) — the site this checkout is on, checked against M's registry of merchant sites: when
  // the money goes to a wallet that isn't the site's, the payee's name is highlighted in yellow and a line says so.
  // The QR carries the site, so the phone checks the same thing (with the Worker, never on the QR's word).
  const site = siteIn === undefined ? hostSite(`${slug}|${reference ?? ""}|${amountStr}`) : siteIn;
  const scanUrl = `${payPageUrl}${payPageUrl.includes("?") ? "&" : "?"}qr=1${site ? `&site=${encodeURIComponent(site)}` : ""}`;
  // 1.5.27 — M's own pay page in a named tab, for the first payment at this merchant (handoff.ts)
  const handoffUrl = `${payPageUrl}?handoff=1${site ? `&site=${encodeURIComponent(site)}` : ""}`;
  let known = !!readCredential();
  // 1.5.48 (Alexander, 31 Oct) — the M® card at a merchant's checkout on the phone: the approver can't be reached from
  // inside the merchant's frame, so above the limit the button stays M's tab's link (the first-payment hand-off) and
  // M's tab does Pay → the passkey → the approver → the card → Paid, and hands back. The frame learns of the card from
  // M's tab's hand-back (its storage is the merchant's partition); until it has, a known wallet pays in place.
  const cardWanted = () => needsCard(readCredential()?.address, Number(amountStr));
  const inPlaceOrCard = () => { if (handOnly || cardWanted()) { payEl.setAttribute("href", handoffUrl); payEl.setAttribute("target", tabName()); payEl.removeAttribute("tabindex"); } else inPlace(); };
  // 1.5.37 (Alexander, 29 Oct: back on the merchant's page from M's tab with the browser's Back, "Pay with M® stops responding"
  // until a reload; with M's ✕ it works) — the link opens M's tab by name, one per order, so a second tap reuses it (1.5.27).
  // M's ✕ closes that tab (window.close); Back leaves it to the browser, and Safari (Back from a tab a link opened returns to
  // the tab that opened it) can keep the named tab out of sight — the next tap then loads into a tab nobody sees. So once the
  // person is back on this page after a hand-off, the name moves on and the next tap opens a fresh tab. (A tab still open
  // behind can still pay: the frame takes M's hand-back from either, and M's tab asks /pay/status before it pays.)
  let handoffN = 0;
  let handedOff = false;
  const tabName = () => handoffTarget(slug, reference) + (handoffN ? `-${handoffN}` : "");

  root.innerHTML = `
    <main class="screen embed${onComputer ? " em-pc" : ""}">
      <div class="em-logo" aria-hidden="true">${LOGO_M}</div>
      <div class="em-line" id="emLine"><span class="em-amt">${esc(fmtAmt(amount))}</span> to <span id="emTo">…</span></div>
      <p id="emWarn" class="em-warn" role="alert" hidden></p>
      ${onComputer ? `<div class="em-qr" id="emQr" data-scan="${esc(scanUrl)}">
        <div class="em-qr-box">${brandedQrSvg(scanUrl, 4, 2)}</div>
        <div class="em-qr-txt"><b>Scan with your phone<br>to pay with M<sup>®</sup></b><span class="muted" id="emQrWait">Waiting for your phone…</span></div>
      </div><p class="em-or" id="emOr"><span>or</span></p>` : ""}
      <!-- 1.5.33 (Alexander, 28 Oct) — nothing covers the order: "You need $0.35 more." in the alert colour, above the button -->
      <p class="em-need" id="emNeed" role="alert" hidden></p>
      <div class="em-row" id="emRow">
        <button class="m-sq-x" id="emX" type="button" aria-label="Close">${IC_X}</button>
        <a class="btn primary em-pay" id="emPay" role="button"${known && !handOnly && !needsCard(readCredential()?.address, Number(amountStr)) ? ` tabindex="0"` : ` href="${esc(handoffUrl)}" target="${esc(tabName())}"`}><span class="mb-x" id="emPayX" role="button" aria-label="Close">${IC_X}</span><span class="em-pay-l">Pay with M<sup>®</sup></span></a>
      </div>
      <!-- 1.5.37 (Alexander, 29 Oct) — once the frame knows the wallet: the muted way to pay from another of the person's M® wallets -->
      <a class="em-other" id="emOther" role="button" href="${esc(handoffUrl)}&amp;pick=1" target="${esc(tabName())}"${known && !handOnly ? "" : " hidden"}>Choose another M<sup>®</sup> wallet</a>
      <div class="em-paid" id="emPaid" hidden><div class="tickbig">✓</div><div class="em-paid-t"><b>Paid</b><span class="muted">with M<sup>®</sup> · receipt on Arc</span></div></div>
      <p id="emSrc" class="status em-status em-src" hidden></p>
      <p id="emStatus" class="status em-status" role="status" aria-live="polite"></p>
    </main>
  `;
  const statusEl = root.querySelector<HTMLElement>("#emStatus")!;
  const main = root.querySelector<HTMLElement>("main.embed")!;
  const toEl = root.querySelector<HTMLElement>("#emTo")!;
  const payEl = root.querySelector<HTMLAnchorElement>("#emPay")!;
  // 1.5.27 — the button says what is happening once tapped (Paying…, in M's neon green); the line under it is left for
  // what needs reading (a shortfall, a refusal, the rate).
  const lblEl = payEl.querySelector<HTMLElement>(".em-pay-l")!;
  const LBL0 = lblEl.innerHTML;
  let busy = false;
  /** 1.5.31 — the button is Add money while this browser's own wallet can't cover the order (see paintShort). */
  let addMode = false;
  // 1.5.39 (Alexander, 29 Oct) — paying: the sound wave across the button in place of the words, as M's tab (1.5.36–37); the
  // words stay in the label, read out, not drawn (.wave)
  const sayOnButton = (text: string | null) => {
    if (text === null) { lblEl.innerHTML = addMode ? "Add money" : LBL0; payEl.classList.remove("busy", "wave"); return; }
    lblEl.textContent = text;
    payEl.classList.add("busy", "wave");
  };
  /** 1.5.27 — the frame in M's world (Midnight Cobalt, lit) from the tap to Paid; the merchant's light panel otherwise. */
  const enterM = (on: boolean) => {
    document.documentElement.setAttribute("data-theme", on ? "dark" : "light");
    main.classList.toggle("em-lit", on);
  };
  const resetButton = () => { busy = false; sayOnButton(null); enterM(addMode); }; // 1.5.33 — Add money stays in Midnight
  /** 1.5.31 — paying in place, the button is a button: no href (href="#" put the frame's address in the computer's status
   *  bar while the pointer was on it — Alexander's recording, 28 Oct), focusable, Enter and Space as a tap. */
  const inPlace = () => { payEl.removeAttribute("href"); payEl.removeAttribute("target"); payEl.setAttribute("tabindex", "0"); };
  payEl.addEventListener("keydown", (e) => { if (!payEl.hasAttribute("href") && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); payEl.click(); } });
  /** 1.5.27 — the wallet is known here now (M's tab handed it back): the button pays in place. */
  const otherEl = root.querySelector<HTMLAnchorElement>("#emOther")!;
  const setKnown = () => {
    known = true;
    if (!paid && !handOnly) otherEl.hidden = false; // 1.5.37; 1.5.54 — on a phone M's tab has "Use another M® wallet"
    if (addMode) return; // 1.5.31 — Add money stays the button until the pockets cover the order
    inPlaceOrCard();
  };
  // 1.5.50 (Alexander, 31 Oct) — the card is the wallet's, not one browser's: the frame reads the known wallet's synced
  // settings (its M® card's public record and the limit, as M's pages do at login) so a frame that never saw the
  // enrolment hands off to M's tab above the limit too. A read that fails leaves what the frame already knows.
  let learnedFor: string | null = null;
  function learnCard(): void {
    const a = readCredential()?.address?.toLowerCase(); if (!a || learnedFor === a) return; learnedFor = a;
    getProfile(a).then((p) => { const { changed } = reconcile((p as { settings?: string | null }).settings); if ((changed.includes("card") || changed.includes("cardAbove")) && known && !addMode && !paid) inPlaceOrCard(); }).catch(() => {});
  }
  if (known) learnCard();
  // v0.99.11 — WebKit on iOS refuses a passkey unless the FRAME's document is
  // focused ("The document is not focused"), and a touch on a div inside an
  // iframe doesn't focus it the way a tap on a page does. Take focus on the
  // person's own touch, and again right before the passkey is asked for.
  const takeFocus = () => {
    try { window.focus(); } catch { /* ignore */ }
    try { payEl.focus({ preventScroll: true }); } catch { /* ignore */ }
  };
  // 1.5.21 (Alexander, 26 Oct) — the muted ✕ in a squircle beside the slider, as in the app: the customer leaves M's
  // checkout; the merchant's page hears "cancelled" (reason "closed") and decides what that means for its order.
  root.querySelector("#emX")?.addEventListener("click", () => post({ event: "cancelled", reason: "closed", ref: reference ?? null }));
  // 1.5.33 (Alexander, 28 Oct: "Kill X squircle") — the ✕ is the button's left segment (the home's centre pill, mirrored);
  // the squircle stays in the DOM, hidden, and the segment is its hand. A tap there never pays or follows the button's link.
  const payXEl = root.querySelector<HTMLElement>("#emPayX")!;
  const stopX = (e: Event) => { e.stopPropagation(); e.preventDefault(); };
  payXEl.addEventListener("pointerdown", (e) => e.stopPropagation());
  payXEl.addEventListener("click", (e) => { stopX(e); if (!busy && !paid) root.querySelector<HTMLButtonElement>("#emX")?.click(); });

  // Tell the host how tall we are, now and whenever it changes.
  const sendHeight = () => post({ event: "height", height: Math.ceil(main.getBoundingClientRect().height) });
  new ResizeObserver(sendHeight).observe(main);

  let toAddress: string | null = null;
  // 1.5.17 — on a computer the QR's poll runs from the moment the payee is known; 1.5.27 — on any device while M's
  // tab is open for the first payment (the frame learns of it from the Worker even when the tab can't tell it).
  let pollTimer: number | undefined;
  let pollStart = 0;
  let polling = false;
  const POLL_MS = 1500, POLL_MAX_MS = 15 * 60 * 1000;
  function pollStatus() {
    if (paid || !reference || polling) return;
    polling = true;
    window.clearTimeout(pollTimer);
    pollStart = pollStart || Date.now();
    const tick = async () => {
      if (paid) return;
      if (document.visibilityState === "visible") {
        try {
          const s = await payStatus(slug, reference!, amountStr, ccy);
          if (s.status === "paid" || s.status === "settled") { paidFromPhone(s); return; }
          if (s.status === "failed") { const w = root.querySelector<HTMLElement>("#emQrWait"); if (w) w.textContent = "That payment didn't go through. Scan again to try once more."; }
        } catch { /* the next tick */ }
      }
      if (Date.now() - pollStart < POLL_MAX_MS) pollTimer = window.setTimeout(tick, POLL_MS);
    };
    pollTimer = window.setTimeout(tick, POLL_MS);
  }
  /** 1.5.27 — Paid: a confirmation, not a slider, in M's world (Midnight Cobalt). */
  function showPaid() {
    root.querySelector<HTMLElement>("#emQr")?.setAttribute("hidden", "");
    root.querySelector<HTMLElement>("#emOr")?.setAttribute("hidden", "");
    root.querySelector<HTMLElement>("#emRow")!.hidden = true;
    root.querySelector<HTMLElement>("#emOther")!.hidden = true; // 1.5.37
    root.querySelector<HTMLElement>("#emSrc")!.hidden = true;
    root.querySelector<HTMLElement>("#emPaid")!.hidden = false;
    statusEl.textContent = "";
    busy = false;
    enterM(true);
    sendHeight();
  }
  let settledPosted = false;
  const postSettled = (tx: string) => { if (settledPosted) return; settledPosted = true; post({ event: "settled", txHash: tx, ref: reference ?? null }); };
  /** Paid somewhere else — the phone that scanned the QR (viaPhone), or M's tab (viaTab, 1.5.27). */
  function paidFromPhone(s: { status: string; txHash?: string | null }, how: { viaTab?: boolean; userOpHash?: string | null } = {}) {
    if (paid) return;
    paid = true;
    window.clearTimeout(pollTimer);
    pollStart = pollStart || Date.now();
    showPaid();
    post({ event: "paid", to: slug, amount: amountStr, currency: ccy, ref: reference ?? null, userOpHash: how.userOpHash ?? null, ...(how.viaTab ? { viaTab: true } : { viaPhone: true }) });
    const settled = postSettled;
    if (s.status === "settled" && s.txHash) { settled(s.txHash); return; }
    // the receipt follows in a second or two
    const again = async () => {
      try { const n = await payStatus(slug, reference!, amountStr, ccy); if (n.status === "settled" && n.txHash) { settled(n.txHash); return; } if (n.status === "failed") { post({ event: "failed", reason: "reverted", txHash: n.txHash ?? undefined }); return; } } catch { /* retry */ }
      if (Date.now() - pollStart < POLL_MAX_MS) pollTimer = window.setTimeout(again, POLL_MS);
    };
    pollTimer = window.setTimeout(again, POLL_MS);
  }
  // v0.99.12 — prepared on load (the frame knows amount and recipient at
  // once), so the slide only signs: Face ID follows the thumb by ~0.75 s
  // instead of ~3.5 s of round trips. Silent; needs the cached credential
  // (no Face ID to prepare). v2.0 — with Circle's stub only (nothing reserved
  // at Circle); the sponsor's signature is asked for when the finger lands on
  // the thumb. No timer: a preparation older than PREPARED_TTL_MS (minutes) is
  // made again on that touch, and the slide waits for it.
  let prep: { address: string; p: PreparedOp } | null = null;
  let prepInFlight: Promise<void> | null = null; // a slide that lands mid-preparation waits for it (v0.99.13)
  function prepare(): Promise<void> {
    if (prepInFlight) return prepInFlight;
    prepInFlight = (async () => {
      if (!toAddress || paid) return;
      const cached = readCredential();
      if (!cached) return;
      try {
        // 1.4.62 — the plan starts from the cached address at once, beside the account build: the pockets are
        // public reads, so the "From your dollars" line is up before the slide is slid. On the phone the card
        // was slid ~2 s after it painted, mid-preparation, and the line only appeared after the thumb (20 Oct).
        const early = cached.address ? planFor(cached.address).catch(() => {}) : null;
        const { address } = await adapter.connectWithCredential(cached);
        // 1.4.58 — the ladder: a shortfall in the request's currency is converted from another pocket inside the
        // payment. Planned here, priced at the slide; with a plan the plain transfer isn't prepared (it would revert).
        if (early) await early;
        await planFor(address);
        if (plan) { prep = null; return; }
        const p = await adapter.prepareTransfer(toAddress, amountStr, ccy);
        prep = { address, p };
      } catch (e) {
        console.warn("[M] embed: prepare failed (the slide will do it)", e);
        prep = null;
      }
    })().finally(() => { prepInFlight = null; });
    return prepInFlight;
  }
  let paid = false;
  let plan: PayPlan | null = null;
  let short = false;
  let plannedFor: string | null = null;
  async function planFor(address: string): Promise<void> {
    address = address.toLowerCase(); // the cached address and the built account's may differ in case only
    if (plannedFor === address) return;
    plannedFor = address; plan = null; short = false;
    const r = await planPay(fxFor(adapter, address), address, ccy, Number(amountStr));
    if (plannedFor !== address) return;
    const srcEl = root.querySelector<HTMLElement>("#emSrc")!;
    if (r.kind === "plan") { plan = r.plan; srcEl.textContent = `From your ${plan.from === "USD" ? "dollars" : plan.from}: ${sourceLine(ccy, plan)}`; srcEl.hidden = false; }
    else { srcEl.hidden = true; if (r.kind === "short") { short = true; paintShort(address, r.need); } }
    sendHeight();
  }
  // 1.4.88 (Alexander, 23 Oct) — short in this browser's own wallet: say so plainly; the way out is Add money — M in a
  // new tab (/add), never a page on the merchant's origin. Back on the merchant's tab, the pockets are read again.
  // Another wallet keeps the ladder's line.
  // 1.5.31 (Alexander, 28 Oct: "once the wallet is linked … check the available balance vs the payment amount — and steer
  // the user to Add money") — the wallet is known here from the second payment at a merchant, so its pockets are read
  // when the checkout opens, the ladder included (a euro order is covered by dollars it can convert). Short: the BUTTON
  // becomes Add money ↗ — the one lit action — and the line above it says why; 1.4.88's dimmed button with a small link
  // under it asked the person to find the way out. Covered again on return: Pay with M® → as before.
  function paintShort(address: string, need: number) {
    void address;
    const needEl = root.querySelector<HTMLElement>("#emNeed")!;
    needEl.textContent = needLine(ccy, need);
    needEl.hidden = false;
    statusEl.textContent = "";
    setAddMode(true);
    sendHeight();
  }
  function setAddMode(on: boolean) {
    addMode = on;
    payEl.classList.toggle("em-add", on);
    lblEl.innerHTML = on ? "Add money" : LBL0;
    enterM(on); // 1.5.33 — Add money in Midnight Cobalt, lit; back to the merchant's light panel once covered
    if (on) { payEl.setAttribute("href", `${location.origin}/add`); payEl.setAttribute("target", "_blank"); payEl.setAttribute("rel", "noopener"); payEl.removeAttribute("tabindex"); }
    else {
      payEl.removeAttribute("rel");
      if (known) inPlaceOrCard();
      else { payEl.setAttribute("href", handoffUrl); payEl.setAttribute("target", tabName()); }
    }
  }
  const recheck = () => {
    if (!short || paid || document.visibilityState !== "visible") return;
    short = false; plannedFor = null; prep = null;
    setAddMode(false);
    statusEl.textContent = "";
    root.querySelector<HTMLElement>("#emNeed")!.hidden = true;
    void prepare().then(sendHeight);
  };
  document.addEventListener("visibilitychange", recheck);
  resolveName(slug).then(
    (r) => {
      toAddress = r.address;
      // On a merchant's page the code never shows: wait for the name.
      // 1.5.39 — the name as this frame last saw it, at once (profileCache); then M's records
      const paintTo = (p: { name?: string | null; code?: string | null; emoji?: string | null }) => { toEl.innerHTML = `<b class="cap">${esc(p.name ?? r.display)}</b> ${emojiFor(p.code ?? r.display, p.emoji)}`; };
      const c = cachedProfile(r.address);
      if (c) paintTo(c);
      getProfile(r.address)
        .then((p) => { rememberProfile(r.address, p); paintTo(p); })
        .catch(() => { if (!c) toEl.innerHTML = `<b class="cap">${esc(r.display)}</b>`; });
      if (site) merchantCheck(site, slug).then((m) => {
        if (m.match !== false || !m.site) return;
        toEl.classList.add("m-mismatch");
        // 1.5.33 — the ✕ is in the button now: the name and the ⚠ line carry the warning
        const w = root.querySelector<HTMLElement>("#emWarn")!;
        w.innerHTML = `<span class="m-warn-ic">${IC_BC_WARNING}</span>${esc(mismatchLine(m.site.name))}`;
        w.hidden = false;
        sendHeight();
      }).catch(() => { /* no answer, nothing to say */ });
      post({ event: "ready", to: slug, amount: amountStr, currency: ccy, ref: reference ?? null });
      sendHeight();
      void prepare();
      if (onComputer) pollStatus(); // 1.5.17 — on a computer, listen for the phone
    },
    () => {
      toEl.textContent = "an unknown code";
      root.querySelector<HTMLElement>("#emRow")!.hidden = true;
      statusEl.textContent = "This merchant's M code wasn't found.";
      post({ event: "failed", reason: "unknown_code" });
    },
  );

  // The fallback for anything the frame can't do: the full pay page, top level.
  // The frame doesn't share the app tab's Lab, so it names the failure itself.
  function offerOpen(line: string, e?: unknown) {
    const why = e instanceof Error ? `${e.name}: ${e.message}`.slice(0, 140) : "";
    statusEl.innerHTML = `${esc(line)} <a href="${esc(payPageUrl)}" target="_top" rel="noopener">Open M to pay</a>${why ? `<br><small class="muted">${esc(why)}</small>` : ""}`;
    post({ event: "open", url: payPageUrl, why });
  }

  async function connectForPay(): Promise<{ address: string }> {
    takeFocus();
    const cached = readCredential();
    if (cached) {
      try { return await adapter.connectWithCredential(cached); }
      catch (e) { console.warn("[M] embed: cached connect failed; using login", e); clearCredential(); }
    }
    const r = await adapter.createOrConnect("login");
    saveCredential({ ...r.credential, address: r.address });
    return { address: r.address };
  }

  async function doTransfer(senderAddress: string) {
    if (!toAddress) throw new Error("no_recipient");
    takeFocus();
    await planFor(senderAddress); // 1.4.58 — another wallet than the cached one: its own pockets
    if (short) throw Object.assign(new Error("short"), { name: "ShortError" });
    let sub: Awaited<ReturnType<typeof adapter.submitTransfer>> | null = null;
    if (plan) {
      const r = await payWithTopUp(fxFor(adapter, senderAddress)!, plan, ccy, toAddress, amountStr); // approve + swap + transfer, one Face ID
      sub = await adapter.submit(r.calls);
      void sub.settled.then(({ txHash, success }) => { if (success) fxFor(adapter, senderAddress)?.reportTopUp?.(r.topUp, txHash, true); }).catch(() => {});
    }
    // Prepared for this wallet and still fresh → sign now (Face ID at once).
    if (prep && prep.address.toLowerCase() === senderAddress.toLowerCase() && Date.now() - prep.p.at < PREPARED_TTL_MS) {
      try { sub = await adapter.sendPrepared(prep.p); }
      catch (e) {
        if (isPasskeyCancel(e)) throw e;
        if (!(e instanceof Error && e.message === "prepared_stale")) recordError("embed/prepared", e);
        sub = null; // refused → the plain path
      }
    }
    if (!sub) {
      sub = await adapter.submitTransfer(toAddress, amountStr, ccy);
    }
    paid = true;
    window.clearTimeout(pollTimer); // 1.5.17 — paid here, not scanned
    // 1.5.12 parked M's "done" here (1.5.10 and 1.5.11 were silent at Omega Air in Safari and Chrome); 1.5.37 plays it from an
    // <audio> element loaded in the tap (sound.ts), once Paid is on screen — below.
    const base = { address: senderAddress, userOpHash: sub.userOpHash, direction: "send" as const, amount: amountStr, counterparty: toAddress, currency: ccy, kind: "pay" as const, ref: reference || undefined };
    recordTx(base).catch((e) => console.warn("[M] tx/record (submit) failed:", e));
    showPaid();
    post({ event: "paid", to: slug, amount: amountStr, currency: ccy, ref: reference ?? null, userOpHash: sub.userOpHash });
    void playDoneElement(); // 1.5.37 — after Paid is painted and the page told: nothing waits for it
    void sub.settled
      .then(({ txHash, success }) => {
        recordTx({ ...base, txHash, success }).catch((e) => console.warn("[M] tx/record (receipt) failed:", e));
        if (!success) { recordError("embed/settle", new Error("userop_reverted " + txHash)); post({ event: "failed", reason: "reverted", txHash }); return; }
        postSettled(txHash);
      })
      .catch((e) => recordError("embed/settle", e));
  }

  // v2.0 — sponsor what is prepared when the finger lands; an old preparation is made again now (the tap waits for it).
  const onTouch = () => {
    if (prep && Date.now() - prep.p.at < PREPARED_TTL_MS) sponsorOnTouch(adapter, prep.p);
    else if (!prepInFlight && !plan && !paid) void prepare().then(() => { if (prep) sponsorOnTouch(adapter, prep.p); });
  };
  payEl.addEventListener("pointerdown", () => { takeFocus(); if (known && !addMode && !handOnly) onTouch(); }, { passive: true }); // 1.5.54 — no sponsorship for a payment made in M's tab
  payEl.addEventListener("click", (e) => {
    // 1.5.31 — Add money: a real link to M's /add in a new tab; the frame reads the pockets again when this tab is back.
    if (addMode) return;
    // 1.5.27 — the first time at this merchant the button is a real link: the browser opens M's tab (a tap on a link is
    // never blocked) and the frame listens — the Worker's /pay/status, and M's tab's hand-back. Nothing changes here.
    if ((!known || handOnly || cardWanted()) && !paid && !busy && toAddress) { handedOff = true; pollStatus(); return; } // 1.5.48 — or the card is needed; 1.5.51 — or a picked wallet's
    e.preventDefault();
    if (paid || busy) return;
    primeDoneElement(); // 1.5.37 — inside the tap, so the element may play at acceptance
    void runPay();
  });
  // 1.5.37 — back on this page after a hand-off (Back, a tab switch, the page restored): the next tap opens a fresh tab
  const freshTab = () => {
    if (!handedOff || paid || (known && !handOnly && !cardWanted()) || document.visibilityState !== "visible") return;
    handedOff = false;
    handoffN++;
    if (!addMode) payEl.setAttribute("target", tabName());
  };
  document.addEventListener("visibilitychange", freshTab);
  window.addEventListener("pageshow", (e) => { if (e.persisted) freshTab(); });
  // 1.5.37 (Alexander, 29 Oct) — Choose another M® wallet: the passkey picker here, in M's frame (M's origin), then the payment
  // from the wallet picked — as M's tab's "Use another M® wallet". The frame keeps the wallet it knows for next time.
  // 1.5.53 (Alexander, 1 Nov: option A) — a link to M's tab (?pick=1), opened in the tap: the picker there, then the payment
  // from the wallet picked — with its card above its limit, which the frame can't take (the approver is a same-tab trip)
  // and could only learn of after the picker, when the tap that may open a tab had been spent on Face ID (a second tap).
  // M's tab hands back paid with no credential of the picked wallet, so the frame keeps the wallet it knows.
  otherEl.addEventListener("click", (e) => {
    if (busy || paid || !toAddress) { e.preventDefault(); return; }
    otherEl.setAttribute("target", tabName());
    handedOff = true; pollStatus();
  });
  // 1.5.27 — M's tab, after the first payment: paid, and the wallet's public values, so the next payment here is in place.
  window.addEventListener("message", (e) => {
    const h = readHandoff(e, location.origin, { to: slug, ref: reference ?? null, amount: amountStr, currency: ccy });
    if (!h) return;
    if (h.card && /^0x[0-9a-fA-F]+$/.test(h.card.publicKey)) { saveCard({ v: 1, ...h.card, publicKey: h.card.publicKey as `0x${string}`, address: h.card.address as `0x${string}` }); try { if (h.cardAbove != null) localStorage.setItem("m.cardAbove", String(h.cardAbove)); } catch { /* ignore */ } } // 1.5.48
    if (h.cred) { saveCredential(h.cred, { ask: false }); setFlag(null); setKnown(); }
    if (h.event === "paid") paidFromPhone({ status: "paid" }, { viaTab: true, userOpHash: h.userOpHash });
    else if (h.txHash) { if (!paid) paidFromPhone({ status: "settled", txHash: h.txHash }, { viaTab: true }); else postSettled(h.txHash); }
  });

  /** 1.5.33 — after a balance refusal: the pockets read again; if the plan still can't say, the pay pocket alone. */
  async function showShortAfterRefusal(): Promise<boolean> {
    const addr = readCredential()?.address?.toLowerCase() ?? prep?.address?.toLowerCase();
    if (!addr) return false;
    plannedFor = null;
    try { await planFor(addr); } catch { /* the fallback below */ }
    if (short) return true;
    let held: number | null = null;
    try { held = Number(await adapter.getBalance(addr, ccy)) || 0; } catch { /* unknown */ }
    const miss = held === null ? Number(amountStr) : Math.ceil((Number(amountStr) - Math.floor(held * 100 + 1e-7) / 100) * 100 - 1e-7) / 100;
    if (miss <= 0) return false; // the reads say it's there and Arc said it isn't: the refusal's own line, as before
    short = true;
    paintShort(addr, miss);
    return true;
  }

  async function runPay() {
    if (!toAddress) return;
    busy = true;
    otherEl.setAttribute("aria-disabled", "true");
    statusEl.textContent = "";
    enterM(true);
    sayOnButton("Paying…");
    try {
      if (prepInFlight) await prepInFlight; // tapped before the preparation finished
      // 1.5.53 — another wallet is chosen in M's tab now (the link #emOther); the frame pays from the wallet it knows
      const sender = prep ? { address: prep.address } : await connectForPay();
      setFlag(null);
      await doTransfer(sender.address);
    } catch (e) {
      otherEl.removeAttribute("aria-disabled");
      resetButton();
      if (e instanceof Error && e.name === "ShortError") return; // 1.4.58 — the status already says what's short
      if (e instanceof Error && e.message === "rate_moved") { // 1.4.60: the line takes the Worker's figure; the next tap settles at it
        if (plan) { const srcEl = root.querySelector<HTMLElement>("#emSrc")!; srcEl.textContent = `From your ${plan.from === "USD" ? "dollars" : plan.from}: ${sourceLine(ccy, plan)}`; }
        statusEl.textContent = "The rate moved — the line shows the new figure. Tap again."; return;
      }
      console.warn("[M] embed: pay failed", e);
      recordError("embed/pay", e);
      // 1.5.33 — Arc refused it for the balance (the check before the tap came back unknown — a read that failed): the
      // Add money state, as a check that had answered would have shown — never "Payment not confirmed" and the
      // merchant's fallback button (Alexander's screenshots of 28 Oct).
      if (failureLine(e) === FAIL_FUNDS && await showShortAfterRefusal()) return;
      if (isPasskeyCancel(e)) {
        // A cancel and a refusal look the same (NotAllowedError); in a frame
        // say which, since the app tab's Lab can't see in here.
        const why = e instanceof Error ? `${e.name}: ${e.message}`.slice(0, 140) : "";
        statusEl.textContent = TAP_AGAIN_LINE;
        post({ event: "cancelled", why });
        return;
      }
      offerOpen("Payment not confirmed.", e);
    }
  }
}

function esc(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);
}
